import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { MatSelectionList } from "@angular/material/list";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";

import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription } from "rxjs";
import {
  catchError,
  finalize,
  map,
  shareReplay,
  startWith,
  take,
  tap,
} from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import {
  DefaultConfirmationDialogData,
  Dialog,
} from "src/app/models/dialog/dialog.model";
import {
  FileOperationStatus,
  PlaylistDTO,
} from "src/app/models/playlists/playlistDTO";
import { MultimediaFileDTO } from "src/app/models/playlists/multimediaFileDTO";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { ManageFileService } from "src/app/shared/services/manage-files/manage-files.service";
import { ManageFilePreviewDialogComponent } from "./manage-file-preview-dialog/manage-file-preview-dialog.component";
import { MatPaginator } from "@angular/material/paginator";
import {
  FieldType,
  FilterName,
} from "src/app/models/custom-table/custom-table.model";
import { select, Store } from "@ngrx/store";
import { loadedTenantState } from "src/app/store/reducers";

@Component({
  selector: "app-manage-files-dialog",
  templateUrl: "./manage-files-dialog.component.html",
  styleUrls: ["./manage-files-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ManageFilesDialogComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);
  loadedPlaylist$: Observable<PlaylistDTO>;
  created$: Observable<boolean>;

  fileList$: Observable<MultimediaFileDTO[]>;
  fileList: MultimediaFileDTO[];
  selectedFiles: string[];

  private uploadingFiles$ = new BehaviorSubject<MultimediaFileDTO[]>([]);
  displayList$: Observable<MultimediaFileDTO[]>;

  // search
  searchTooltipText: string;
  searchTerms: string[] = [];
  private lastArgs: {
    paging?: any;
    sorting?: any;
    filtering?: any;
    advancedFiltering?: any;
  } = {};

  // pagination
  totalFiles = 0;
  pageIndex = 0;
  pageSize = 50;

  // uploading
  public busy: Record<string, boolean> = {};
  private setFileBusy(id?: string, isFileProcessing = false) {
    if (id) {
      this.busy[id] = isFileProcessing;
    }
  }

  selectedTab: "image" | "video" = "image";
  tabLockType: "image" | "video" | null = null;

  //Thumbnail
  private thumbnailCache: { [fileId: string]: SafeUrl } = {};
  public fileThumbnails: { [fileId: string]: Observable<SafeUrl> } = {};

  selectedUploadFile: File;
  allowMultiple: boolean;
  fileType: string;

  private initialIdsSelected: string[] = [];
  idsSelected: string[] = [];

  fileOperationStatus = FileOperationStatus;

  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  dateTimeFormat: string;
  timezone: string;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild("selectionList", { static: false })
  selectionList?: MatSelectionList;

  constructor(
    private store: Store,
    private manageFileService: ManageFileService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<ManageFilesDialogComponent>
  ) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
  }

  ngOnInit(): void {
    const incomingType = (this.data?.fileType || "").toLowerCase();
    this.tabLockType = incomingType.includes("video")
      ? "video"
      : incomingType.includes("image")
      ? "image"
      : null;

    this.loadTenantDateTimeFomat();

    this.selectedTab = this.tabLockType ?? "image";

    this.idsSelected = this.data.treatedDtoSelection ?? [];
    this.allowMultiple = this.data.allowMultiple ?? true;
    this.fileType = this.data.fileType;
    this.initialIdsSelected = [...this.idsSelected];

    this.loadFiles({
      paging: { skip: 0, top: this.pageSize, realSize: true },
      sorting: this.lastArgs.sorting,
      filtering: this.lastArgs.filtering,
      advancedFiltering: this.lastArgs.advancedFiltering,
    });

    this.setSearchTooltip();
  }

  setSearchTooltip() {
    this.translate
      .get("SimpleFilter.HintTitle")
      .subscribe((translatedTitle) => {
        const searchTooltipHint = translatedTitle;
        const searchableFields = [ this.translate.instant("Terminal.name") ];
        this.searchTooltipText = `${searchTooltipHint}\n${searchableFields.join(
          "\n"
        )}`;
      });
  }

  canSelect(type: "image" | "video"): boolean {
    return !this.tabLockType || this.tabLockType === type;
  }

  onNavSelect(type: "image" | "video") {
    if (!this.canSelect(type) || this.selectedTab === type) return;
    this.selectedTab = type;
    this.idsSelected = [];
    this.initialIdsSelected = [];
    this.selectedFiles = [];
    this.selectedUploadFile = undefined;
    this.clearThumbnailCache();
    this.loadFiles();
  }

  get hasSelectionChanges(): boolean {
    const key = (arr?: string[] | null) =>
      Array.from(new Set((arr ?? []).map(String)))
        .sort()
        .join("|");
    return key(this.idsSelected) !== key(this.initialIdsSelected);
  }

  onOverlayToggle(payload: { id: string; checked: boolean }) {
    const { id, checked } = payload || {};
    if (!id) return;
    const before = [...(this.idsSelected ?? [])];

    if (this.allowMultiple) {
      this.idsSelected = checked
        ? Array.from(new Set([...(this.idsSelected ?? []), id]))
        : (this.idsSelected ?? []).filter((x) => x !== id);
    } else {
      this.idsSelected = checked ? [id] : [];
    }
  }

  get fileHintKey(): string {
    return this.selectedTab === "video"
      ? "Playlist.fileMaxSize"
      : "Playlist.fileMaxSizeImage";
  }

  openPreview(file: any) {
    const id = file?.id;
    if (!id) return;

    const name: string = file?.name ?? "";
    const ext = (name.split(".").pop() || "").toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);

    this.dialog.open(ManageFilePreviewDialogComponent, {
      width: "80vw",
      maxWidth: "900px",
      panelClass: "media-preview-dialog",
      data: { id, name, kind: isImage ? "image" : "video" },
    });
  }

  getPagedData() {
    const { pageIndex, pageSize } = this.paginator;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.loadFiles({
      ...this.lastArgs,
      paging: { skip: pageIndex * pageSize, top: pageSize, realSize: true },
    });
  }

  private buildExtensionAdvancedFilter():
    | { advancedFilter: any[] }
    | undefined {
    const allowedFileExtensions = this.getAllowedExtensions();
    if (!allowedFileExtensions.length) return undefined;

    const operator = (FilterName as any)?.ENDS_WITH ?? FilterName.CONTAINS;

    return {
      advancedFilter: allowedFileExtensions.map((ext) => ({
        column: "name",
        fieldType: FieldType.STRING,
        operator,
        value: ext,
      })),
    };
  }

  loadFiles(args?: {
    paging?: any;
    sorting?: any;
    filtering?: any;
    advancedFiltering?: any;
  }): void {
    if (args) this.lastArgs = args;
    this.isLoading$.next(true);

    const fileExtensionFilterConfig = this.buildExtensionAdvancedFilter();
    const finalAdvancedFilter =
      fileExtensionFilterConfig || this.lastArgs.advancedFiltering
        ? {
            advancedFilter: [
              ...(this.lastArgs.advancedFiltering?.advancedFilter ?? []),
              ...(fileExtensionFilterConfig?.advancedFilter ?? []),
            ],
          }
        : undefined;

    this.fileList$ = this.manageFileService
      .getFilesList2(
        this.lastArgs.paging,
        this.lastArgs.filtering,
        this.lastArgs.sorting,
        finalAdvancedFilter
      )
      .pipe(
        map((resp: any) => {
          const items = resp?.items ?? [];
          this.totalFiles = resp?.size ?? resp?.total ?? items.length;

          this.fileList = items;
          return items;
        }),
        startWith(this.fileList ?? []),
        catchError(() => of(this.fileList ?? [])),
        finalize(() => this.isLoading$.next(false))
      );

    this.displayList$ = combineLatest([
      this.uploadingFiles$,
      this.fileList$,
    ]).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      map(([pending, files]) => [...pending, ...files]));
  }

  private getAllowedExtensions(): string[] {
    if (!this.fileType) return [];

    const type = this.fileType?.toLowerCase();

    if (type.includes("image")) {
      return [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    } else if (type.includes("video")) {
      return [".mp4", ".avi", ".mov", ".mkv", ".webm"];
    } else {
      return [];
    }
  }

  selectUploadFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    this.selectedUploadFile = undefined;

    if (!file) {
      if (input) input.value = "";
      return;
    }

    const tempId = this.addPendingUpload(file);
    this.saveUploadFile(file, tempId);

    if (input) input.value = "";
  }

  private addPendingUpload(file: File): string {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const pending: any = {
      id: tempId,
      name: file.name,
      fileStatus: "UPLOADING",
      uploading: true,
    };
    const curr = this.uploadingFiles$.value;
    this.uploadingFiles$.next([pending, ...curr]);
    return tempId;
  }

  private removePendingUpload(tempId: string): void {
    const curr = this.uploadingFiles$.value;
    this.uploadingFiles$.next(curr.filter((f) => f.id !== tempId));
  }

  saveUploadFile(file: File, tempId?: string) {
    if (!file) return;

    const allowedExtensions = this.getAllowedExtensions();
    const extension = "." + (file.name.split(".").pop()?.toLowerCase() || "");

    if (!allowedExtensions.includes(extension)) {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.fileWrongType"),
        type: SnackbarType.ERROR,
      });
      if (tempId) {
        this.removePendingUpload(tempId);
      }
      return;
    }

    const { size } = file;
    if (size > 209715200) {
      this.snackbarService.show({
        message: this.translate.instant("Playlist.fileMaxSizeExceeded"),
        type: SnackbarType.ERROR,
      });
      if (tempId) {
        this.removePendingUpload(tempId);
      }
      return;
    }

    this.manageFileService
      .createFile(file)
      .pipe(
        tap(() => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileUploaded"),
            type: SnackbarType.SUCCESS,
          });
          this.clearThumbnailCache();
          this.loadFiles();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error) ?? this.translate.instant("Playlist.fileUploadFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => {
          if (tempId) {
            this.removePendingUpload(tempId);
          }
        })
      )
      .subscribe();
  }

  downloadFile(file: any): void {
    const id = file?.id;
    if (!id) return;
    this.setFileBusy(id, true);
    this.manageFileService
      .getFileThumbnail(id, false)
      .pipe(finalize(() => this.setFileBusy(id, false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name || "file";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileDownloadFail"),
            type: SnackbarType.ERROR,
          });
        },
      });
  }

  openFileDeleteDialog(file: MultimediaFileDTO) {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteFile(file.id);
      }
    });
  }

  deleteFile(id: string) {
    if (!id) return;
    this.setFileBusy(id, true);
    this.manageFileService
      .deleteFile(id)
      .pipe(
        tap(() => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.fileDeleted"),
            type: SnackbarType.SUCCESS,
          });
          this.loadFiles();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: error ? this.translate.instant(error) : this.translate.instant("Playlist.fileDeleteFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setFileBusy(id, false))
      )
      .subscribe();
  }

  getThumbnail(fileId: string): Observable<SafeUrl> {
    if (!fileId) return of(null as any);

    if (this.thumbnailCache[fileId]) {
      return of(this.thumbnailCache[fileId]);
    }

    if (!this.fileThumbnails[fileId]) {
      this.fileThumbnails[fileId] = this.manageFileService
        .getFileThumbnail(fileId)
        .pipe(
          take(1),
          map((blob) => {
            const url = URL.createObjectURL(blob);
            const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
            this.thumbnailCache[fileId] = safeUrl;
            return safeUrl;
          }),
          shareReplay(1)
        );
    }

    return this.fileThumbnails[fileId];
  }

  onSearchChanged(terms: string[]) {
    this.searchTerms = (terms || [])
      .map((t) => (t || "").trim())
      .filter(Boolean);

    const filtering = this.searchTerms.length
      ? { filterConfig: [{ field: "name" }], searchTerms: this.searchTerms }
      : undefined;

    this.pageIndex = 0;
    this.paginator?.firstPage?.();

    this.loadFiles({
      filtering,
      sorting: this.lastArgs.sorting,
      paging: { skip: 0, top: this.pageSize, realSize: true },
    });
  }

  isDisabledFile(_file: any): boolean {
    return false;
  }

  checkSelected(file: any): boolean {
    const id = file?.id;
    const res = !!id && this.idsSelected.includes(id);
    return res;
  }

  public onConfirmClick(): void {
    const selectedFileObjects = this.fileList
      .filter((file) => this.idsSelected.includes(file.id))
      .map((file) => ({ id: file.id, name: file.name }));

    this.dialogRef.close({
      submitted: true,
      selectedFiles: selectedFileObjects,
    });
  }

  public onCancelClick(): void {
    this.dialogRef.close({ submitted: false });
  }

  clearThumbnailCache(): void {
    this.thumbnailCache = {};
    this.fileThumbnails = {};
  }

  loadTenantDateTimeFomat(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateTimeFormat = data.datetimeFormat;
        this.timezone = data.timezone;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
  }
}
